import { createAction, props } from '@ngrx/store';
import { SystemError } from 'src/app/shared/models/SystemError';
import { DWSMConfig } from '../models/DWSMConfig';
import { Plant } from '../models/Plant';
import { PlantCCDC } from '../models/PlantCCDC';
import { PsaAdmin } from '../models/PsaAdmin';
import { SearchPlantParams } from '../models/SearchPlantParams';
import { Technology } from '../models/Technology';
import { DefConfigVersion } from '../models/config-version/DefConfigVersion';
import { Building } from '../models/Building';
import { ProductLine } from '../models/ProductLine';
import { Derivative } from '../models/Derivative';

export enum ActionTypes {
    LOAD_PLANTS = '[Plants API] Loading Plants',
    LOAD_PLANTS_SUCCESS = '[Plants API] Plants data fetched successfully',
    LOAD_PLANTS_FAIL = '[Plants API]  Plants data fetch failed',
    LOAD_TECHNOLOGIES = '[Technologies API] Loading Technologies',
    LOAD_TECHNOLOGIES_SUCCESS = '[Technologies API] Technologies data fetched successfully',
    LOAD_TECHNOLOGIES_FAIL = '[Technologies API] Technologies data fetch failed',
    LOAD_PSA_ADMIN = '[PSA API] Load PSA Admin',
    LOAD_PSA_ADMIN_SUCCESS = '[PSA API] PSA Admin data fetched successfully',
    LOAD_PSA_ADMIN_FAIL = '[PSA API] PSA Admin data fetch failed',
    SEARCH_PLANT_CCDC = '[Plant CCDC API] Search Plant CCDC',
    SEARCH_PLANT_CCDC_SUCCESS = '[Plant CCDC API] Plant CCDC data fetched successfully',
    SEARCH_PLANT_CCDC_FAIL = '[Plant CCDC API] Plant CCDC data fetch failed',
    SEARCH_VALID_PLANT_CCDC = '[Plant CCDC API] Search valid Plant CCDC',
    SEARCH_VALID_PLANT_CCDC_SUCCESS = '[Plant CCDC API] Valid Plant CCDC data fetched successfully',
    SEARCH_VALID_PLANT_CCDC_FAIL = '[Plant CCDC API] Valid Plant CCDC data fetch failed',
    SEARCH_PLANT_CCDC_BY_PLANT_IDS = '[Plant CCDC API] Search Plant CCDC by plant ids',
    SEARCH_PLANT_CCDC_BY_PLANT_IDS_SUCCESS = '[Plant CCDC API] Plant CCDC data fetched successfully by plant ids',
    SEARCH_PLANT_CCDC_BY_PLANT_IDS_FAIL = '[Plant CCDC API] Plant CCDC data fetch failed by plant ids',
    CLEAR_PLANT_CCDC = '[Plant CCDC API] Clear plant ccdc',
    FIND_DWSM_GLOBAL_CONFIG = '[DWSM Api] Loading DWSM global config',
    FIND_DWSM_GLOBAL_CONFIG_SUCCESS = '[DWSM Api] DWSM global config fetched successfully',
    FIND_DWSM_GLOBAL_CONFIG_FAIL = '[DWSM Api] DWSM global config fetch failed',
    GET_ACTIVE_CONFIG_VERSION_SUCCESS = '[Core] Active config version loaded successfully',
    GET_ACTIVE_CONFIG_VERSION_FAIL = '[Core] Active config version failed to load',
    GET_CONFIG_VERSION_BY_ID = '[Analysis API] Loading config version by id',
    GET_CONFIG_VERSION_BY_ID_SUCCESS = '[Analysis Page] Config version by id loaded successfully',
    GET_CONFIG_VERSION_BY_ID_FAIL = '[Analysis API] Config version by id failed to load',
    LOAD_OPTIONAL_DATA = '[Building & Product Line API] Loading Buildings and Product Lines data',
    LOAD_OPTIONAL_DATA_SUCCESS = '[Building & Product Line API] Buildings and Product Lines data fetched successfully',
    LOAD_OPTIONAL_DATA_FAIL = '[Building & Product Line API] Buildings and Product Lines data fetch failed',
    SEARCH_PRODUCT_LINES = '[Product Line API] Search Product Lines',
    SEARCH_PRODUCT_LINES_SUCCESS = '[Product Line API] Product Line data fetched successfully',
    SEARCH_PRODUCT_LINES_FAIL = '[Product Line API] Product Line data fetch failed',
    SEARCH_DERIVATIVE = '[Derivative API] Search Derivative',
    SEARCH_DERIVATIVE_SUCCESS = '[Derivative API] Derivative data fetched successfully',
    SEARCH_DERIVATIVE_FAIL = '[Derivative API] Derivative data fetch failed',
    SEARCH_BUILDING = '[Building API] Search Building',
    SEARCH_BUILDING_SUCCESS = '[Building API] Building data fetched successfully',
    SEARCH_BUILDING_FAIL = '[Building API] Building data fetch failed',
    CLEAR_DERIVATIVES = '[Derivatives] Clear Derivatives'
}

export const loadPlants = createAction(ActionTypes.LOAD_PLANTS, props<{ searchPlantParams: SearchPlantParams }>());

export const loadPlantsSuccess = createAction(ActionTypes.LOAD_PLANTS_SUCCESS, props<{ plants: Plant[] }>());

export const loadPlantsFail = createAction(ActionTypes.LOAD_PLANTS_FAIL);

export const loadTechnologies = createAction(
    ActionTypes.LOAD_TECHNOLOGIES,
    props<{
        searchTechnologyParams: SearchPlantParams;
    }>()
);

export const loadTechnologiesSuccess = createAction(
    ActionTypes.LOAD_TECHNOLOGIES_SUCCESS,
    props<{
        technologies: Technology[];
    }>()
);

export const loadTechnologiesFail = createAction(ActionTypes.LOAD_TECHNOLOGIES_FAIL);

export const loadPsaAdmin = createAction(ActionTypes.LOAD_PSA_ADMIN);

export const loadPsaAdminSuccess = createAction(ActionTypes.LOAD_PSA_ADMIN_SUCCESS, props<{ psaAdmin: PsaAdmin[] }>());

export const loadPsaAdminFail = createAction(ActionTypes.LOAD_PSA_ADMIN_FAIL);

export const searchPlantCCDC = createAction(ActionTypes.SEARCH_PLANT_CCDC, props<{ plantCodes: string[] }>());

export const searchPlantCCDCSuccess = createAction(
    ActionTypes.SEARCH_PLANT_CCDC_SUCCESS,
    props<{
        plants: PlantCCDC[];
    }>()
);

export const searchPlantCCDCFail = createAction(ActionTypes.SEARCH_PLANT_CCDC_FAIL);

export const searchValidPlantCCDC = createAction(ActionTypes.SEARCH_VALID_PLANT_CCDC, props<{ plantCodes: string[] }>());

export const searchValidPlantCCDCSuccess = createAction(
    ActionTypes.SEARCH_VALID_PLANT_CCDC_SUCCESS,
    props<{
        plants: PlantCCDC[];
    }>()
);

export const searchValidPlantCCDCFail = createAction(ActionTypes.SEARCH_VALID_PLANT_CCDC_FAIL);

export const searchPlantCCDCByPlantIds = createAction(ActionTypes.SEARCH_PLANT_CCDC_BY_PLANT_IDS, props<{ plantIds: number[] }>());

export const searchPlantCCDCByPlantIdsSuccess = createAction(ActionTypes.SEARCH_PLANT_CCDC_BY_PLANT_IDS_SUCCESS, props<{ plants: PlantCCDC[] }>());

export const searchPlantCCDCByPlantIdsFail = createAction(ActionTypes.SEARCH_PLANT_CCDC_BY_PLANT_IDS_FAIL, props<{ systemError: SystemError }>());

export const clearPlantCCDC = createAction(ActionTypes.CLEAR_PLANT_CCDC);

export const findDwsmGlobalConfig = createAction(ActionTypes.FIND_DWSM_GLOBAL_CONFIG);

export const findDwsmGlobalConfigSuccess = createAction(
    ActionTypes.FIND_DWSM_GLOBAL_CONFIG_SUCCESS,
    props<{ dwsmConfig: DWSMConfig; message: string | null }>()
);

export const findDwsmGlobalConfigFail = createAction(ActionTypes.FIND_DWSM_GLOBAL_CONFIG_FAIL, props<{ systemError: SystemError }>());

export const getActiveConfigVersionSuccess = createAction(ActionTypes.GET_ACTIVE_CONFIG_VERSION_SUCCESS, props<{ activeConfigVersion: DefConfigVersion }>());

export const getActiveConfigVersionFail = createAction(ActionTypes.GET_ACTIVE_CONFIG_VERSION_FAIL, props<{ systemError: SystemError }>());

export const getConfigVersionById = createAction(ActionTypes.GET_CONFIG_VERSION_BY_ID, props<{ id: number }>());

export const getConfigVersionByIdSuccess = createAction(ActionTypes.GET_CONFIG_VERSION_BY_ID_SUCCESS, props<{ configVersionById: DefConfigVersion }>());

export const getConfigVersionByIdFail = createAction(ActionTypes.GET_CONFIG_VERSION_BY_ID_FAIL, props<{ systemError: SystemError }>());

export const loadOptionalData = createAction(ActionTypes.LOAD_OPTIONAL_DATA, props<{ plantCode: string; productLineCodes: string[] | undefined }>());

export const loadOptionalDataSuccess = createAction(
    ActionTypes.LOAD_OPTIONAL_DATA_SUCCESS,
    props<{ buildings: Building[]; productLines: ProductLine[]; derivatives: Derivative[] }>()
);

export const loadOptionalDataFail = createAction(ActionTypes.LOAD_OPTIONAL_DATA_FAIL);

export const searchProductLines = createAction(ActionTypes.SEARCH_PRODUCT_LINES);

export const searchProductLinesSuccess = createAction(ActionTypes.SEARCH_PRODUCT_LINES_SUCCESS, props<{ productLines: ProductLine[] }>());

export const searchProductLinesFail = createAction(ActionTypes.SEARCH_PRODUCT_LINES_FAIL);

export const searchBuilding = createAction(ActionTypes.SEARCH_BUILDING, props<{ plantCode: string }>());

export const searchBuildingSuccess = createAction(ActionTypes.SEARCH_BUILDING_SUCCESS, props<{ buildings: Building[] }>());

export const searchBuildingFail = createAction(ActionTypes.SEARCH_BUILDING_FAIL);

export const searchDerivative = createAction(ActionTypes.SEARCH_DERIVATIVE, props<{ productLineCodes: string[] }>());

export const searchDerivativeSuccess = createAction(ActionTypes.SEARCH_DERIVATIVE_SUCCESS, props<{ derivatives: Derivative[] }>());

export const searchDerivativeFail = createAction(ActionTypes.SEARCH_DERIVATIVE_FAIL);

export const clearDerivatives = createAction(ActionTypes.CLEAR_DERIVATIVES);
